import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  Modal,
  TextField,
  Select,
  MenuItem,
  Button,
  Paper,
  Dialog,
} from "@mui/material";
import DealEmailTemplate from "../../Settings/components/DealEmailTemplate";
import { useSelector } from "react-redux";
import {
  postStageTransfer,
  updateStageTransfer,
} from "../../../redux/modules/deals";
import { AddCircleOutline } from "@mui/icons-material";
import { useDispatch } from "react-redux";
import CloseIcon from "@mui/icons-material/Close";
import { getBTemplates } from "../../../redux/modules/email";
import { parseTimeFrame } from "../../../utils/format_date";
import { setAlert } from "../../../redux/modules/alert";

const OverrideRulesModal = ({
  open,
  handleClose,
  selectedStage,
  selectedPipeStages,
  handleCloseOverRide,
  stageRulesData,
}) => {
  const dispatch = useDispatch();
  const [transferData, setTransferData] = useState({
    id: "",
    emailTemplateId: "",
    followUpTime: "",
    followUpUnit: "",
    moveToStage: "",
    autoMoveTime: "",
    autoMoveUnit: "",
    senderName: "",
  });

  const [openTemplate, setOpenTemplate] = useState(false);
  const [loading, setLoading] = useState(false);
  const bodyTemplates = useSelector((state) => state?.email?.bodyTemplates);

  const handleInputChange = (field, value) => {
    setTransferData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const resetInitialState = () => {
    setTransferData({
      id: "",
      emailTemplateId: "",
      followUpTime: "",
      followUpUnit: "",
      moveToStage: "",
      autoMoveTime: "",
      autoMoveUnit: "",
    });
  };

  useEffect(() => {
    if (stageRulesData && stageRulesData.length > 0) {
      const firstRule = stageRulesData[0];
      const formattedFollowup = parseTimeFrame(firstRule?.follow_up_time || "");
      const formattedAutomove = parseTimeFrame(
        firstRule?.stage_transition_time || ""
      );

      setTransferData({
        id: firstRule?.id || "",
        emailTemplateId: firstRule?.deal_template_id || "",
        followUpTime: formattedFollowup?.value || "",
        followUpUnit:
          formattedFollowup?.unit === "h"
            ? "hours"
            : formattedFollowup?.unit === "m"
            ? "minute"
            : "days" || "",
        moveToStage: firstRule?.deal_stage_to || "",
        autoMoveTime: formattedAutomove?.value || "",
        autoMoveUnit:
          formattedAutomove?.unit === "h"
            ? "hours"
            : formattedAutomove?.unit === "m"
            ? "minute"
            : "days" || "",
        senderName: firstRule?.sender_name,
      });
    } else {
      resetInitialState();
    }
  }, [stageRulesData]);

  const onEmailtempOpen = (stage) => {
    setOpenTemplate(true);
  };

  const onEmailtempClose = () => {
    setOpenTemplate(false);
  };

  const dealStages = selectedPipeStages?.filter(
    (_, index) =>
      index >
      selectedPipeStages.findIndex(
        (stage) => stage.stage_id === selectedStage?.stage_id
      )
  );

  const handleOverRide = () => {
    const {
      autoMoveTime,
      autoMoveUnit,
      followUpTime,
      followUpUnit,
      moveToStage,
      emailTemplateId,
      senderName,
    } = transferData;

    const formattedAutoMoveTime = `${autoMoveTime}${
      autoMoveUnit === "days" ? "d" : autoMoveUnit === "hours" ? "h" : "m"
    }`;
    const formattedFollowUpTime = `${followUpTime}${
      followUpUnit === "days" ? "d" : followUpUnit === "hours" ? "h" : "m"
    }`;

    const basePayload = {
      deal_stage_from: selectedStage?.stage_id,
      deal_stage_to: moveToStage,
      stage_transition_time: formattedAutoMoveTime,
      follow_up_time: formattedFollowUpTime,
      template_id: emailTemplateId,
    };

    if (selectedStage?.rule_id) {
      dispatch(
        updateStageTransfer({
          ...basePayload,
          sender_name: senderName,
          tbl_id: selectedStage.rule_id,
        })
      )
        .then(() => {
          resetInitialState();
          handleClose();
          handleCloseOverRide();
        })
        .catch((error) => {
          dispatch(setAlert(error?.message || `Error in inserting`, "error"));
        });
    } else {
      dispatch(
        postStageTransfer({
          ...basePayload,
          ...(senderName && { sender_name: senderName }),
          pipeline_id: selectedStage?.pipeline_id,
        })
      )
        .then((res) => {
          handleClose();
          handleCloseOverRide();
          resetInitialState();
        })
        .catch((error) => {
          dispatch(setAlert(error?.message || `Error in inserting`, "error"));
        });
    }
  };

  const isSaveDisabled = () => {
    return (
      !transferData.moveToStage ||
      !transferData.autoMoveTime ||
      !transferData.autoMoveUnit ||
      !transferData.emailTemplateId ||
      !transferData.followUpTime ||
      !transferData.followUpUnit
    );
  };

  return (
    <div>
      <Dialog
        open={open}
        onClose={() => {
          handleClose();
          handleCloseOverRide();
          resetInitialState();
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { overflowY: "hidden", position: "relative" }, // position: relative for absolute close button
        }}
      >
        {/* Close Button */}
        <IconButton
          onClick={() => {
            handleClose();
            handleCloseOverRide();
            resetInitialState();
          }}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>

        <Box
          sx={{
            boxShadow: 24,
            p: 2,
            borderRadius: 4,
          }}
        >
          <Typography variant="h6" sx={{ marginBottom: 3 }} gutterBottom>
            Override Follow-Up Rules
          </Typography>
          <Paper
            style={{
              gridColumn: "1 / span 4",
              marginTop: 10,
              paddingLeft: 15,
              paddingRight: 15,
              marginBottom: 10,
              paddingTop: 10,
              paddingBottom: 10,
            }}
          >
            {/* Overlayed Section for Follow-Up and Auto-Move Logic */}
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: "10px",
                marginTop: 12,
                flexDirection: "column",
                marginBottom: 20,
              }}
            >
              <Typography variant="caption" fontWeight="bold">
                Auto-Move Settings
              </Typography>

              <Select
                size="small"
                value={transferData.moveToStage || ""}
                onChange={(e) =>
                  handleInputChange("moveToStage", e.target.value)
                }
                displayEmpty
              >
                <MenuItem value="" disabled>
                  Select Stage
                </MenuItem>
                {dealStages.map((stage) => (
                  <MenuItem key={stage.stage_id} value={stage.stage_id}>
                    {stage.stage_name}
                  </MenuItem>
                ))}
              </Select>

              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  flexWrap: "wrap",
                  gap: "10px",
                  width: "100%",
                }}
              >
                <div style={{ width: "62%" }}>
                  <TextField
                    label="After"
                    type="number"
                    size="small"
                    value={transferData.autoMoveTime || ""}
                    onChange={(e) =>
                      handleInputChange("autoMoveTime", e.target.value)
                    }
                    inputProps={{
                      min: 1,
                      onWheel: (e) => e.target.blur(),
                    }}
                    onKeyDown={(e) => {
                      if (
                        e.key === "-" || // prevent negative
                        e.key === "e" || // prevent scientific notation
                        e.key === "." || // prevent decimals
                        (e.key === "0" && e.target.value.length === 0) // prevent starting with 0
                      ) {
                        e.preventDefault();
                      }
                    }}
                    sx={{ width: "100%" }}
                  />
                </div>
                <div style={{ width: "36%" }}>
                  <Select
                    size="small"
                    value={transferData?.autoMoveUnit || ""}
                    displayEmpty
                    sx={{
                      width: "100%",
                    }}
                    onChange={(e) =>
                      handleInputChange("autoMoveUnit", e.target.value)
                    }
                    error={
                      Boolean(transferData?.autoMoveTime) &&
                      !transferData?.autoMoveUnit
                    }
                  >
                    <MenuItem value="" disabled>
                      Select Unit
                    </MenuItem>
                    <MenuItem value="days">Day</MenuItem>
                    <MenuItem value="hours">Hour</MenuItem>
                    <MenuItem value="minute">Minute</MenuItem>
                  </Select>
                </div>
              </Box>
            </div>
            <div>
              <Typography variant="caption" fontWeight="bold">
                Follow-Up Email Settings
              </Typography>
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  flexDirection: "column",
                  gap: "10px",
                  marginTop: 8,
                }}
              >
                <Select
                  size="small"
                  value={transferData?.emailTemplateId || ""}
                  displayEmpty
                  renderValue={(selected) => {
                    if (!selected) {
                      return <span>Select Email Template</span>;
                    }
                    const template = bodyTemplates?.find(
                      (t) => String(t.tb_id) === selected
                    );

                    return (
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Typography>{template?.name}</Typography>
                        <Box
                          sx={{
                            fontSize: 12,
                            fontWeight: 600,
                            color:
                              template?.is_plain_email === "1"
                                ? "#2e7d32"
                                : "#1565c0",
                            backgroundColor:
                              template?.is_plain_email === "1"
                                ? "#E8F5E9"
                                : "#E3F2FD",
                            borderRadius: "10px",
                            px: 1,
                            py: "2px",
                          }}
                        >
                          {template?.is_plain_email === "1"
                            ? "Plain"
                            : "Branded"}
                        </Box>
                      </Box>
                    );
                  }}
                  onChange={(e) => {
                    const value = e.target.value;

                    if (value === "add_new_template") {
                      onEmailtempOpen();
                    } else {
                      handleInputChange("emailTemplateId", String(value));
                    }
                  }}
                >
                  {bodyTemplates?.length > 0
                    ? [
                        <MenuItem key="placeholder" value="" disabled>
                          Select Email Template
                        </MenuItem>,
                        <MenuItem
                          value="add_new_template"
                          sx={{ color: "#1976d2" }}
                        >
                          <AddCircleOutline fontSize="small" sx={{ mr: 1 }} />{" "}
                          Add New Template
                        </MenuItem>,
                        <MenuItem disabled>
                          <hr
                            style={{
                              width: "100%",
                              border: "none",
                              borderTop: "1px solid #ddd",
                            }}
                          />
                        </MenuItem>,
                        ...bodyTemplates?.map((template) => (
                          <MenuItem
                            key={template?.tb_id}
                            value={String(template?.tb_id)}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                width: "100%",
                              }}
                            >
                              <Typography>{template?.name}</Typography>
                              <Box
                                sx={{
                                  fontSize: 12,
                                  fontWeight: 600,
                                  color:
                                    template.is_plain_email === "1"
                                      ? "#2e7d32"
                                      : "#1565c0",
                                  backgroundColor:
                                    template.is_plain_email === "1"
                                      ? "#E8F5E9"
                                      : "#E3F2FD",
                                  borderRadius: "10px",
                                  px: 1,
                                  py: "2px",
                                }}
                              >
                                {template?.is_plain_email === "1"
                                  ? "Plain"
                                  : "Branded"}
                              </Box>
                            </Box>
                          </MenuItem>
                        )),
                      ]
                    : [
                        <MenuItem key="not-found" disabled>
                          No templates found
                        </MenuItem>,
                      ]}
                </Select>

                <TextField
                  label="Email Sender Name"
                  type="text"
                  size="small"
                  placeholder="Leave empty to use default company name"
                  value={transferData.senderName || ""}
                  onChange={(e) => {
                    const value = e.target.value;
                    const filteredValue = value
                      .replace(/[^a-zA-Z\s]/g, "")
                      .slice(0, 50);
                    handleInputChange("senderName", filteredValue);
                  }}
                  style={{ width: "100%" }}
                  inputProps={{
                    maxLength: 50,
                  }}
                />

                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    gap: "10px",
                    width: "100%",
                  }}
                >
                  <TextField
                    label="Send Email After"
                    type="number"
                    size="small"
                    value={transferData.followUpTime || ""}
                    onChange={(e) =>
                      handleInputChange("followUpTime", e.target.value)
                    }
                    style={{ width: "64%" }}
                    inputProps={{
                      min: 1,
                      onWheel: (e) => e.target.blur(),
                    }}
                    onKeyDown={(e) => {
                      if (
                        e.key === "-" || // prevent negative
                        e.key === "e" || // prevent scientific notation
                        e.key === "." || // prevent decimals
                        (e.key === "0" && e.target.value.length === 0) // prevent starting with 0
                      ) {
                        e.preventDefault();
                      }
                    }}
                  />
                  <Select
                    sx={{
                      width: "34%",
                    }}
                    size="small"
                    value={transferData?.followUpUnit || ""}
                    displayEmpty
                    onChange={(e) =>
                      handleInputChange("followUpUnit", e.target.value)
                    }
                    error={
                      Boolean(transferData.followUpTime) &&
                      !transferData.followUpUnit
                    }
                  >
                    <MenuItem value="" disabled>
                      Select Unit
                    </MenuItem>
                    <MenuItem value="days">Day</MenuItem>
                    <MenuItem value="hours">Hour</MenuItem>
                    <MenuItem value="minute">Minute</MenuItem>
                  </Select>
                </Box>
              </div>
            </div>
            <DealEmailTemplate
              open={openTemplate}
              close={onEmailtempClose}
              lead={{}}
              loadingDU={loading}
              multiple
              stage={selectedStage}
            />
          </Paper>

          <Box display="flex" justifyContent="flex-end" gap={2} mt={3}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleOverRide}
              disabled={isSaveDisabled()}
            >
              {selectedStage?.rule_id ? "Update" : "Save"}
            </Button>
            <Button
              variant="outlined"
              onClick={() => {
                handleClose();
                handleCloseOverRide();
                resetInitialState();
              }}
            >
              Cancel
            </Button>
          </Box>
        </Box>
      </Dialog>
    </div>
  );
};

export default OverrideRulesModal;
